import type React from 'react';
import { useResponsiveBreakpoint } from '@squareup/ui-responsive-window';
import { FdicDisclosure } from '../fdic-disclosure/FdicDisclosure';
import type { ValuePropositionsProps } from '../value-propositions/ValuePropositions';
import { FrequentlyAskedQuestions, type FrequentlyAskedQuestion } from '../frequently-asked-questions/FrequentlyAskedQuestions';
import { PageLayout, PageLayoutVariant } from '../page-layout/PageLayout';
import styles from './ProductLandingPage.module.css';

interface ProductLandingPageProps {
  readonly goBack?: () => void;
  readonly heading: string;
  readonly locationSelector?: JSX.Element;
  readonly title: string | React.ReactNode;
  readonly subtitle: string | React.ReactNode;
  readonly cta?: React.ReactNode;
  readonly image: React.ReactNode;
  readonly showFdic?: boolean;
  readonly featureSection?: React.ReactElement<ValuePropositionsProps>;
  readonly footer: React.ReactNode;
  readonly disclaimer?: string | React.ReactNode;
  readonly frequentlyAskedQuestions?: FrequentlyAskedQuestion[];
  readonly testId?: string;
}

export const ProductLandingPage = ({
  goBack,
  heading,
  locationSelector,
  title,
  subtitle,
  cta,
  image,
  showFdic = false,
  featureSection,
  footer,
  disclaimer,
  frequentlyAskedQuestions,
  testId = 'product-landing-page',
} : ProductLandingPageProps) => {
  const { isMobile } = useResponsiveBreakpoint();

  return (
    <PageLayout
      testId={testId}
      heading={heading}
      variant={PageLayoutVariant.LandingPage}
      goBack={goBack}
      locationSelector={locationSelector}
      footer={footer}
    >
      <section className={styles.hero}>
        <div className={styles.heroText}>
          <h1 className={isMobile ? 'display-10' : 'display-20'}>{title}</h1>
          <p className={`paragraph-30 text-10 ${styles.subtitle}`}>{subtitle}</p>
          {cta && <div className={styles.ctaContainer}>{cta}</div>}
          {disclaimer && <p className={`paragraph-20 ${styles.disclaimer}`}>{disclaimer}</p>}
          {showFdic && <FdicDisclosure />}
        </div>
        <div data-testid="product-landing-page-image" className={styles.heroImage}>
          {image}
        </div>
      </section>
      {featureSection && (
        <div data-testid="feature-section">{featureSection}</div>
      )}
      {frequentlyAskedQuestions && (
        <FrequentlyAskedQuestions frequentlyAskedQuestions={frequentlyAskedQuestions} />
      )}
    </PageLayout>
  );
};
